import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { updateDescription } from '../../AssignmentsApi';
import { useState } from 'react';

// will path as props
// 1- assignmentId
// 2- description = assignment.task_details.Description 
export default function AssignmentDescription({ assignmentId, description }) {
    const queryClient = useQueryClient();
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');


    // Update description mutation
    const updateDescriptionMutation = useMutation({
        mutationFn: (newDescription) => updateDescription(assignmentId, newDescription),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['assignmentDetails', assignmentId]
            });
            setIsEditingDescription(false);
        },
        onError: (error) => {
            console.error("Error updating description:", error);
            alert("Failed to update description. Please try again.");
        }
    });

    // Handlers
    const handleEditDescription = () => {
        setEditedDescription(description || '');
        setIsEditingDescription(true);
    };

    const handleSaveDescription = () => {
        // if (editedDescription.trim() === '') {
        //     alert("Description cannot be empty");
        //     return;
        // }
        updateDescriptionMutation.mutate(editedDescription);
    };

    const handleCancelEdit = () => {
        setIsEditingDescription(false);
        setEditedDescription('');
    };


    return (
        <motion.div
            className="p-4 rounded-3"
            style={{
                border: '1px solid var(--hairline)',
                backgroundColor: 'white'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
        >
            <div className='d-flex align-items-center justify-content-between mb-3'>
                <h5 className="m-0" style={{ color: 'var(--ink)', fontWeight: '700' }}>
                    <i className="fas fa-align-left me-2" style={{ color: 'var(--accent)' }}></i>
                    Description
                </h5>
                {!isEditingDescription && (
                    <motion.button
                        className="btn btn-sm d-flex align-items-center"
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid var(--hairline)',
                            color: 'var(--ink)',
                            borderRadius: '8px',
                            fontWeight: '600',
                            padding: '6px 16px'
                        }}
                        onClick={handleEditDescription}
                        whileHover={{
                            borderColor: 'var(--accent)',
                            backgroundColor: 'rgba(199, 29, 150, 0.02)'
                        }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <i className="fa-solid fa-pen-to-square me-2" style={{ fontSize: '0.85rem' }}></i>
                        Edit
                    </motion.button>
                )}
            </div>

            <AnimatePresence mode="wait">
                {isEditingDescription ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <textarea
                            className="form-control mb-3"
                            rows="6"
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            disabled={updateDescriptionMutation.isPending}
                            style={{
                                border: '1px solid var(--hairline)',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                color: 'var(--ink)',
                                resize: 'vertical'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = 'var(--accent)';
                                e.target.style.boxShadow = '0 0 0 3px rgba(199, 29, 150, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = 'var(--hairline)';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                        <div className='d-flex gap-2'>
                            <motion.button
                                className='btn d-flex align-items-center px-4'
                                style={{
                                    backgroundColor: 'var(--accent)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: '600'
                                }}
                                onClick={handleSaveDescription}
                                disabled={updateDescriptionMutation.isPending}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {updateDescriptionMutation.isPending ? (
                                    <>
                                        <i className="fa-solid fa-spinner fa-spin me-2"></i>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-check me-2"></i>
                                        Save Changes
                                    </>
                                )}
                            </motion.button>
                            <motion.button
                                className='btn d-flex align-items-center px-4'
                                style={{
                                    backgroundColor: 'white',
                                    color: 'var(--ink)',
                                    border: '1px solid var(--hairline)',
                                    borderRadius: '8px',
                                    fontWeight: '600'
                                }}
                                onClick={handleCancelEdit}
                                disabled={updateDescriptionMutation.isPending}
                                whileHover={{ backgroundColor: 'var(--surface-sunken)' }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Cancel
                            </motion.button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <p
                            className="m-0"
                            style={{
                                color: 'var(--ink)',
                                fontSize: '1rem',
                                fontWeight: '500',
                                lineHeight: '1.65',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {description || (
                                <span style={{ color: 'var(--ink-soft)', fontStyle: 'italic', fontWeight: '500' }}>
                                    No description provided
                                </span>
                            )}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}
